import { useEffect, useState, useMemo } from "react";
import { SearchCountResponse } from "../../../../../common/api/decimal/methods/search";
import ResultSection from "../../common/ResultSection";
import { FormattedMessage } from "react-intl";
import clsx from "clsx";
import { ManageAssetWithLicense } from "../../../../../common/api/decimal/common-types/manageAssets";
import ListContentResultCard from "../ListContentResultCard";
import styles from "./AllContentResults.module.scss";
import LoadableAllContentResults from "./LoadableAllContentResults";
import LoaderButton from "../../../../../common/core-ui/LoaderButton";
import PaddedDotLoader from "../../common/PaddedDotLoader";
import { useSearchCountsWithNextCursorFetcher } from "../searchCounts";
import { useSearchParams } from "react-router-dom";
import { makeSearchReturnDetail, SearchSection } from "../types.ts";

export default function AllContentResults({
  initialResults,
  initialNextCursor,
  openPanel,
}: {
  initialResults: SearchCountResponse[];
  initialNextCursor: string | undefined;
  openPanel: (asset: ManageAssetWithLicense, section: SearchSection) => void;
}) {
  const [nextCursor, setNextCursor] = useState<string | undefined>(
    initialNextCursor
  );
  const [additionalChunkCount, setAdditionalChunkCount] = useState(0);
  const [newResultsAreLoading, setNewResultsAreLoading] = useState(false);
  const fetcher = useSearchCountsWithNextCursorFetcher();
  const [searchParams] = useSearchParams();
  const hasResults = useMemo(
    () =>
      !!initialResults.length || !!fetcher.data?.results?.allResults?.length,
    [initialResults, fetcher]
  );

  useEffect(() => {
    if (fetcher.state === "idle" && fetcher.data) {
      setNextCursor(fetcher.data.results?.nextCursor);
    }
  }, [fetcher]);

  useEffect(() => {
    const fetchResults = (cursor: string) => {
      fetcher.load(
        `/search/api/paginated-counts/${cursor}?${searchParams.toString()}`
      );
    };

    if (
      !initialResults.length &&
      initialNextCursor &&
      fetcher.state === "idle" &&
      !fetcher.data
    ) {
      fetchResults(initialNextCursor);
    }

    if (
      fetcher.state === "idle" &&
      fetcher.data?.results?.nextCursor &&
      !fetcher.data.results.allResults?.length
    ) {
      fetchResults(fetcher.data.results.nextCursor);
    }
  }, [initialResults, nextCursor, searchParams, fetcher, initialNextCursor]);

  if (!hasResults) {
    if (nextCursor) {
      return <PaddedDotLoader />;
    }
    return null;
  }

  return (
    <ResultSection>
      <FormattedMessage defaultMessage="All Content Results" tagName="h2" />
      <FormattedMessage defaultMessage="Results ordered A to Z" tagName="p" />
      <ul className={styles.list}>
        {initialResults?.map((result) => (
          <ListContentResultCard
            searchResult={result}
            openPanel={openPanel}
            key={result.asset.manageAsset.vbid}
            section={"all"}
            libraryReturn={makeSearchReturnDetail(
              "all",
              result.asset.manageAsset.vbid
            )}
          />
        ))}
        {fetcher.data?.results?.allResults?.map((result) => (
          <ListContentResultCard
            searchResult={result}
            openPanel={openPanel}
            key={result.asset.manageAsset.vbid}
            section={"all"}
            libraryReturn={makeSearchReturnDetail(
              "all",
              result.asset.manageAsset.vbid
            )}
          />
        ))}
        {Array.from({ length: additionalChunkCount }).map((_, i) => (
          <LoadableAllContentResults
            cursor={nextCursor}
            setNextCursor={setNextCursor}
            openPanel={openPanel}
            key={i}
            setIsLoading={setNewResultsAreLoading}
          />
        ))}
      </ul>
      {hasResults && nextCursor && (
        <LoaderButton
          className={clsx(
            "vst-button-pill",
            "vst-button-variant-outline",
            styles.loadMoreButton
          )}
          onClick={() => setAdditionalChunkCount(additionalChunkCount + 1)}
          loading={newResultsAreLoading}
          loaderProps={{ color: "thematic" }}
        >
          <FormattedMessage defaultMessage="Load More" />
        </LoaderButton>
      )}
    </ResultSection>
  );
}
